import { Flex, Grid, View } from '@adobe/react-spectrum';
import { error } from '@react/react-spectrum/Toast';
import DateIcon from '@spectrum-icons/workflow/Date';
import FileCodeIcon from '@spectrum-icons/workflow/FileCode';
import qs from 'query-string';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { commonApi, excApi, handleApiCall } from '@exchange-frontends/api';
import {
  Carousel,
  CarouselItem,
  DetailsSectionView,
  GeolocationContext,
  WithSkeleton,
} from '@exchange-frontends/components';
import { useAnalyticsPageLoad, useMediaQuery } from '@exchange-frontends/custom-hooks';
import { azSort, detailsPage, handleAppDetailsForLaunch } from '@exchange-frontends/utils';

import { DetailHeading } from '../../../../components/DetailHeading';
import { ContainerContext } from '../../../../utils';
import { useStore } from '../../store';
import parseHTML from '../../utils/ParseHTML';
import { dateFormatter } from '../../utils/dateFormatter';
import { concatTags } from '../../utils/general';
import { UserDataContext } from '../App';
import Header from './Header';
import NotAvailableInEUBanner from './NotAvailableInEUBanner';
import PublisherApps from './PublisherApps';
import SlnDetails from './SlnDetails';
import { RatingsAndReviews } from './ratings/RatingsAndReviews';
import SideBar from './sidebar/SideBar';

/**
 * @typedef {'cc' | 'ec' | 'dc'} CloudType
 * @typedef {Object} AppDetailsContextType
 * @property {CloudType} [cloud]
 * @property {boolean} [isPreview]
 * @property {boolean} [isEUUser]
 * @property {boolean} [isUnavailableInEU]
 * @property {boolean} [fallsUnderDigitalServicesAct]
 */

/** @type {React.Context<AppDetailsContextType>} */
export const AppDetailsContext = React.createContext({});

// Styled Components
/**
 * main details container
 * @memberof AppDetails
 * @type {styled-component}
 */
const DetailApp = styled.div`
  padding-top: 20px;
  margin: 0 auto;
  max-width: 1280px;
  width: 90%;
`;

/**
 * styled text span
 * @memberof AppDetails
 * @type {styled-component}
 */

const InfoText = styled.div`
  font-size: var(--spectrum-global-dimension-static-font-size-100);
  max-width: 848px;
  overflow-wrap: break-word;
  white-space: pre-wrap;
`;

const StyledImage = styled.div`
  width: 100%;
  padding-bottom: 56.25%;
`;

const GRID_AREAS = {
  oneColumn: ['banner', 'carousel', 'main', 'solutionDetails', 'article', 'findIt', 'sidebar', 'ratings', 'moreApps'],
  twoColumn: [
    'banner banner',
    'carousel sidebar',
    'main sidebar',
    'solutionDetails sidebar',
    'article sidebar',
    'findIt sidebar',
    'ratings sidebar',
    'moreApps sidebar',
  ],
};

const getGridAreas = (isSingleColumn, cloud, shouldShowBannerSection) => {
  let areas = isSingleColumn ? GRID_AREAS.oneColumn : GRID_AREAS.twoColumn;

  if (cloud !== 'cc') {
    areas = areas.filter((area) => !area.includes('ratings'));
  }

  if (!shouldShowBannerSection) {
    areas = areas.filter((area) => !area.includes('banner'));
  }

  return areas;
};

/**
 * AppDetails container
 * @component
 * @category Experience Cloud Details
 * @subcategory Details
 * @param {Object} props
 * @param {function} props.setEncounteredError
 * @param {CloudType} props.cloud
 * @param {boolean} [props.isIntegration]
 * @param {boolean} [props.isPreview]
 */
const AppDetails = ({ setEncounteredError, cloud, isIntegration, isPreview }) => {
  /** id and versionId of the app from the url params */
  const { pluginType = 'UXP', id, versionId } = useParams();

  const { profile } = useContext(UserDataContext) ?? {};
  const { geolocation } = useContext(GeolocationContext) ?? {};
  const { ims } = useContext(ContainerContext);
  const accessToken = useMemo(() => ims?.getAccessToken()?.token, [ims]);

  const location = useLocation();
  const navigate = useNavigate();

  const carouselPosition = qs.parse(location.search).mediaIndex;
  const workflow = qs.parse(location.search).workflow;

  /** media query hook to watch for max-width changes */
  const isSingleColumn = useMediaQuery('(max-width: 1000px)');

  const detailScreen = useStore((state) => state.detailScreen);
  const detailScreenLoading = useStore((state) => state.detailScreenLoading);
  const [captions, setCaptions] = useState(null);

  useAnalyticsPageLoad(detailsPage, !detailScreenLoading);
  const fallsUnderDigitalServicesAct = !isPreview && detailScreen?.pricingInfo?.price !== undefined;
  const isEUUser = geolocation === 'EU';
  const isUnavailableInEU = fallsUnderDigitalServicesAct && isEUUser && !detailScreen?.publisher?.contactDetails;
  const isSolution = detailScreen?.appType === 'SLN';

  // TODO: This and the carousel need their own component
  // And this needs to be refactored to use regex
  const formatVideoUrl = useCallback(
    (url = '', index) => {
      let transformedUrl;
      const videoFormatRules = new Map([
        ['watch?v=', 'embed/'],
        ['youtu.be', 'youtube.com/embed/'],
        ['vimeo.com', 'player.vimeo.com/video'],
      ]);

      videoFormatRules.forEach((val, key) => {
        const urlsWithValidSubstringList = [
          'player.vimeo.com',
          'youtu.be/embed',
          'youtube.com/embed',
          'video.tv.adobe.com',
        ];
        //Check if Url plays in Iframe without formating the Url
        const containsValidUrl = urlsWithValidSubstringList.find((subStr) => {
          return url.includes(subStr);
        });
        if (containsValidUrl) {
          transformedUrl = url;
        } else if (url?.includes(key)) {
          if (url?.includes('vimeo') && url?.includes('review')) {
            transformedUrl = url.replace(
              /https:\/\/vimeo.com\/.*\/review\/(.*)\/.*/,
              'https://player.vimeo.com/video/$1'
            );
          } else if (url?.includes('watch?v=')) {
            const {
              query: { v },
            } = qs.parseUrl(url);
            if (carouselPosition) {
              transformedUrl =
                index == carouselPosition
                  ? `https://youtube.com/embed/${v}?autoplay=1&mute=1`
                  : `https://youtube.com/embed/${v}`;
            } else {
              transformedUrl = `https://youtube.com/embed/${v}`;
            }
          } else {
            transformedUrl = url?.replace(key, val);
          }
        }
      });
      return transformedUrl;
    },
    [carouselPosition]
  );

  // current implantation of carousel requires to send captions in a separate Array
  const getCaptionsFromAssets = (mediaAssets = []) => {
    const captionsList = [];
    mediaAssets?.map((media) => {
      if (media.type === 'preview' || media.type === 'video') {
        const caption = media.caption ?? '';
        captionsList.push(caption);
      }
    });
    return captionsList;
  };

  // Fetch EC/DC/CC page details
  useEffect(() => {
    let mounted = true;
    let handler = null;
    if (isPreview) {
      if (accessToken) {
        handler = handleApiCall(excApi.getPreviewDetails, {
          type: cloud,
          appId: id,
          versionId,
          accessToken,
          pluginType,
        });
      }
    } else if (cloud?.toLowerCase() === 'cc') {
      if (isIntegration) {
        handler = handleApiCall(excApi.appIntegrationDetails, { appId: id });
      } else {
        handler = handleApiCall(commonApi.ccesV3Extensions, {
          appId: id,
          resultType: 'details',
          authToken: accessToken,
        });
      }
    } else if (cloud?.toLowerCase() === 'ec' || cloud?.toLowerCase() === 'dc') {
      handler = handleApiCall(excApi.getECAppDetails, { id: id, appType: cloud, authToken: accessToken });
    }
    if (id && handler) {
      useStore.setState({ detailScreenLoading: true });
      handler
        .run()
        .then((data) => {
          const detailData = data.apps[0];
          if (!detailData) {
            navigate(`../../404/not-found`);
            return;
          }
          if (mounted) {
            /* Since there is no id associated with media, index of the media asset(only preview and video) 
            is used in order to preview youtube video in exchange */
            const formattedMedia = detailData?.[carouselPosition ? 'mediaList' : 'assets']?.reduce(
              (assetGroup, item, index) => {
                assetGroup[item.type] = assetGroup[item.type] || [];
                if (item.type === 'preview') {
                  item.image = item.url;
                }
                if (item.type == 'video') {
                  item.url = formatVideoUrl(item?.url, index);
                  assetGroup['preview'] = assetGroup['preview'] ? assetGroup['preview'] : [];
                  assetGroup['preview'].push(item);
                }
                assetGroup[item.type].push(item);
                return assetGroup;
              },
              {}
            );
            if (carouselPosition) {
              formattedMedia['icon'] = [];
              formattedMedia['documents'] = [];
              detailData?.assets?.filter((asset) => {
                if (asset.type === 'icon') {
                  formattedMedia['icon'].push(asset);
                } else if (asset.type === 'document') {
                  formattedMedia['documents'].push(asset);
                }
              });
            }
            if (detailData?.appType === 'ABD') {
              detailData.hostApps = [...(detailData?.hostApps ?? []), 'ABD'];
            }
            detailData.media = formattedMedia;
            detailData.media?.document?.sort((a, b) => a.order - b.order);
            detailData.media?.preview?.sort((a, b) => a.order - b.order);
            setCaptions(getCaptionsFromAssets(detailData?.media?.preview));
            detailData.languages = detailData?.languages?.sort(azSort);
            useStore.setState({
              detailScreen: detailData,
              assetVersion: detailData?.version,
              overallRating: detailData?.rating ?? 0,
              histogram: detailData?.histogram,
              totalRatings: Object.values(detailData?.histogram ?? {}).reduce((prev, current) => prev + current, 0),
              detailScreenLoading: false,
            });
          }
        })
        .catch((err) => {
          // CCES asked us to redirect to login on 429
          if (err.code === 429) {
            if (!ims?.isMock) {
              ims.signIn();
            } else {
              error('You need to login in order to continue', { timeout: 0 });
              setEncounteredError(true);
            }
          } else if (err.code === 404) {
            navigate(`../../404/not-found`);
            // 20 is the error code for aborted requests
          } else if (err.code !== 20) {
            error(err.userMessage, { timeout: 0 });
            console.error(err);
            setEncounteredError(true);
          }
        });
    }
    return () => {
      handler?.controller.abort();
      mounted = false;
    };
  }, [
    id,
    cloud,
    isIntegration,
    carouselPosition,
    accessToken,
    versionId,
    isPreview,
    pluginType,
    ims,
    navigate,
    formatVideoUrl,
    setEncounteredError,
  ]);

  useEffect(() => {
    if (workflow === 'share' && id) {
      let mounted = true;
      const handler = handleApiCall(commonApi.getCreativeCloudLink, {
        route: 'pluginDetail',
        action: 'share',
        params: { pluginId: id },
      });
      (async () => {
        const response = await handler.run();
        if (mounted) {
          window.location.href = `${response?.deeplinkUrl}`;
        }
      })();
      return () => {
        mounted = false;
        handler.controller.abort();
      };
    }
  }, [id, workflow]);

  // get cc category names
  useEffect(() => {
    if (cloud === 'cc') {
      let mounted = true;
      const handler = handleApiCall(commonApi.getCCMostPopularTags);
      (async () => {
        try {
          const tags = await handler.run();
          if (mounted && tags) {
            useStore.setState({
              ccCategories: tags,
            });
          }
        } catch (err) {
          console.error(err);
        }
      })();
      return () => {
        mounted = false;
        handler.controller.abort();
      };
    }
  }, [cloud]);

  // set isUserPartOfOrg flag
  useEffect(() => {
    if (cloud === 'cc' && id && accessToken) {
      let mounted = true;
      const handler = handleApiCall(excApi.checkIfUserIsInOrganization, {
        appId: id,
        accessToken,
        imsUserId: profile?.userId,
        imsEmail: profile?.email,
      });
      (async () => {
        try {
          const isUserPartOfOrg = await handler.run();
          if (mounted && isUserPartOfOrg) {
            useStore.setState({
              isUserPartOfOrg,
            });
          }
        } catch (err) {
          console.error(err);
        }
      })();
      return () => {
        mounted = false;
        handler.controller.abort();
      };
    }
  }, [cloud, id, accessToken, profile?.userId, profile?.email]);

  const shouldShowUpdatedDate = cloud === 'cc' && detailScreen?.lastUpdatedDate;

  // scroll to a specific section of the page
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!detailScreenLoading && !scrolled) {
      const id = location.hash.replace('#', '');
      if (id) {
        const element = document.getElementById(id);
        if (element) {
          window.scrollTo({
            behavior: 'smooth',
            top: element.offsetTop,
          });
          setScrolled(true);
        }
      }
    }
  }, [location.hash, detailScreenLoading, scrolled]);

  return (
    <AppDetailsContext.Provider value={{ cloud, isPreview, isEUUser, isUnavailableInEU, fallsUnderDigitalServicesAct }}>
      <View data-testingid="app-details" minHeight="100vh">
        <Helmet>
          <title>{`${detailScreen?.name ?? ''} | Adobe Exchange`}</title>
          <meta name="robots" content="noodp, FOLLOW, INDEX" />
          <meta name="title" content={`${detailScreen?.name ?? ''} | Adobe Exchange`} />
          <meta property="og:title" content={`${detailScreen?.name ?? ''} | Adobe Exchange`} />
          <meta name="description" content={detailScreen?.description} />
          <meta property="og:description" content={detailScreen?.description} />
          <meta name="keywords" content={concatTags(detailScreen?.standardTags, detailScreen?.customTags).join(',')} />
          <meta property="og:type" content="article" />
          {/* use featured image if available, otherwise use first preview image */}
          <meta
            property="og:image"
            content={
              detailScreen?.assets?.find((a) => a.type === 'featured')?.url ||
              detailScreen?.assets?.sort((a, b) => a.order - b.order).find((a) => a.type === 'preview')?.url
            }
          />
          <meta
            name="thumbnailUrl"
            content={
              detailScreen?.assets?.find((a) => a.type === 'featured')?.url ||
              detailScreen?.assets?.sort((a, b) => a.order - b.order).find((a) => a.type === 'preview')?.url
            }
          />
          <meta property="serp-content-type" content="partner_extensions" />
        </Helmet>
        {/* HEADER */}
        <Header />
        <Flex alignItems="center" direction="column">
          <DetailApp onClickCapture={handleAppDetailsForLaunch}>
            <Grid
              data-testid="app-details-grid"
              areas={getGridAreas(isSingleColumn, cloud, isUnavailableInEU)}
              columns={isSingleColumn ? ['100%'] : ['3fr', 'minmax(304px, 1fr)']}
              rows={['auto']}
              gap="size-400"
            >
              {isUnavailableInEU && (
                <View gridArea="banner">
                  <NotAvailableInEUBanner marginBottom="8px" marginTop="20px" />
                </View>
              )}
              {/* MAIN */}
              {/* CAROUSEL */}
              <WithSkeleton shouldRender={!detailScreenLoading} height={500}>
                {detailScreen?.media?.preview && (
                  <Carousel gridArea="carousel" width="100%" captions={captions} carouselPosition={carouselPosition}>
                    {detailScreen?.media?.preview?.map((media, idx) => (
                      <CarouselItem key={media?.url}>
                        {media.type === 'preview' ? (
                          <StyledImage
                            role="img"
                            aria-label={captions?.[idx] || `Preview image ${idx + 1}`}
                            style={{
                              background: `url("${media?.url}") center center / contain no-repeat`,
                            }}
                          />
                        ) : (
                          <div style={{ paddingBottom: '56.25%' }}>
                            <iframe
                              style={{
                                position: 'absolute',
                                height: '100%',
                                width: '100%',
                              }}
                              src={media?.url}
                              frameBorder="0"
                              allowFullScreen
                              title={`Showing ${media?.name} preview ${idx + 1} video for ${detailScreen?.name} app`}
                            />
                          </div>
                        )}
                      </CarouselItem>
                    ))}
                  </Carousel>
                )}
              </WithSkeleton>
              {(detailScreen?.description?.trim() || detailScreenLoading) && (
                <View data-testid="app-details-grid-main" gridArea="main">
                  {/* INFO */}
                  <DetailHeading level={4}>{isSolution ? 'Solution description' : 'Info'}</DetailHeading>
                  <DetailsSectionView data-testid="app-details-description">
                    <WithSkeleton shouldRender={!detailScreenLoading} count={10}>
                      {isIntegration && (
                        <>
                          <InfoText>{detailScreen?.summary}</InfoText>
                          <br />
                        </>
                      )}
                      <InfoText>{parseHTML(detailScreen?.description)}</InfoText>
                    </WithSkeleton>
                  </DetailsSectionView>
                </View>
              )}

              {isSolution && <SlnDetails />}

              {/*trim is added since release notes might consist of "/r"*/}
              {(detailScreen?.releaseNote?.trim() || detailScreenLoading || shouldShowUpdatedDate) && (
                <View gridArea="article">
                  {/* WHAT'S NEW */}
                  <DetailHeading level={4}>What&apos;s new</DetailHeading>
                  <DetailsSectionView>
                    <WithSkeleton shouldRender={!detailScreenLoading} count={10}>
                      {shouldShowUpdatedDate && (
                        <Flex
                          data-testid="updated-section"
                          justifyContent="space-between"
                          alignItems="start"
                          gap="size-200"
                          wrap="wrap"
                          marginBottom="size-200"
                        >
                          <Flex alignItems="center" gap="size-100">
                            <DateIcon size="S" />
                            <strong>Updated {dateFormatter.format(new Date(detailScreen?.lastUpdatedDate))}</strong>
                          </Flex>
                          {detailScreen?.version && (
                            <Flex alignItems="center" gap="size-100">
                              <FileCodeIcon size="S" />
                              <strong>Version {detailScreen?.version}</strong>
                            </Flex>
                          )}
                        </Flex>
                      )}
                      <InfoText>{parseHTML(detailScreen?.releaseNote?.trim())}</InfoText>
                    </WithSkeleton>
                  </DetailsSectionView>
                </View>
              )}
              {(detailScreen?.whereToFind || detailScreenLoading) && (
                <View gridArea="findIt">
                  {/* WHAT'S NEW */}
                  <DetailHeading level={4}>Where to find it</DetailHeading>
                  <DetailsSectionView>
                    <WithSkeleton shouldRender={!detailScreenLoading} count={10}>
                      <InfoText>{parseHTML(detailScreen?.whereToFind?.replace('&#013;', '<br/>') ?? '')}</InfoText>
                    </WithSkeleton>
                  </DetailsSectionView>
                </View>
              )}
              {cloud === 'cc' && !isIntegration && !isPreview && (
                <View gridArea="ratings" id="reviews">
                  <DetailHeading level={4}>Ratings &amp; reviews</DetailHeading>
                  <DetailsSectionView>
                    <RatingsAndReviews />
                  </DetailsSectionView>
                </View>
              )}
              {/* MORE BY Publisher */}
              <PublisherApps />
              {/* SIDEBAR */}
              <View data-testid="app-details-grid-sidebar" gridArea="sidebar">
                <SideBar />
              </View>
            </Grid>
          </DetailApp>
        </Flex>
      </View>
    </AppDetailsContext.Provider>
  );
};

export default AppDetails;
